// src/pages/shared/useSharedTournament.js
// Loads a shared tournament by its link token and keeps it up to date while the page is visible.
// Public: no sign-in, the token in the link is the credential.
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../../config';

export const REFRESH_MS = 15000;

// status: 'loading' | 'ok' | 'stale' (could not refresh, showing the last data) | 'error' | 'gone' (link not active)
export function useSharedTournament(token, refreshMs = REFRESH_MS) {
    const [state, setState] = useState({ data: null, status: 'loading', fetchedAt: null });
    const latest = useRef(0);

    const load = useCallback(async () => {
        const request = ++latest.current; // a slow, older answer must not overwrite a newer one
        try {
            const response = await fetch(`${API_URL}/share/${encodeURIComponent(token)}`);
            if (request !== latest.current) return;
            if (response.status === 404) {
                setState({ data: null, status: 'gone', fetchedAt: null });
            } else if (!response.ok) {
                throw new Error(`Failed to load (${response.status})`);
            } else {
                setState({ data: await response.json(), status: 'ok', fetchedAt: new Date() });
            }
        } catch (error) {
            if (request !== latest.current) return;
            setState((previous) => ({ ...previous, status: previous.data ? 'stale' : 'error' }));
        }
    }, [token]);

    useEffect(() => {
        load();
        const timer = setInterval(() => {
            if (document.visibilityState !== 'hidden') load();
        }, refreshMs);
        const onVisible = () => {
            if (document.visibilityState === 'visible') load(); // coming back to the tab: catch up right away
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [load, refreshMs]);

    return { ...state, reload: load };
}
